import { useEffect } from "react";
import { SITE_NAME, DEFAULT_OG_IMAGE, SITE_URL } from "../data/seo";

// Upsert a <meta> tag keyed by name or property.
function upsertMeta(attr, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertCanonical(href) {
  if (!href) return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

// Full per-route metadata manager: title, description, canonical,
// Open Graph, Twitter Card, and JSON-LD (single object or array).
export default function usePageMeta({
  title,
  description,
  canonical,
  image,
  type = "website",
  jsonLd,
}) {
  useEffect(() => {
    let ogImage = image || DEFAULT_OG_IMAGE;
    if (ogImage && ogImage.startsWith("/")) ogImage = SITE_URL + ogImage;
    if (title) document.title = title;
    if (description) upsertMeta("name", "description", description);
    if (canonical) upsertCanonical(canonical);

    // Open Graph
    upsertMeta("property", "og:site_name", SITE_NAME);
    if (title) upsertMeta("property", "og:title", title);
    if (description) upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    if (canonical) upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    if (title) upsertMeta("name", "twitter:title", title);
    if (description) upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    // JSON-LD (page-scoped; cleaned up on unmount)
    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const scripts = blocks.map((block) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-page-schema", "true");
      s.text = JSON.stringify(block);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
  }, [title, description, canonical, image, type, jsonLd]);
}
